/*
  customsirge_pdp_main.js
  Purpose:  Behaviour for the PDP main section: the gallery scroll-swap
            (client answer 2026-09-15, Q2), the selling-plan cards, and
            variant switching. Registers ONE component, 'pdp_main'
            (docs/CONVENTIONS.md #4 — JS registers, never auto-runs).
  Spec:     .claude/knowledge/extraction/figma/sections/pdp_main/BRIEF.md
            "## Client answers" (gallery swap, variant-change event).
  Data:     none ambient — reads only data-* attributes and DOM state under
            the matched section root.
  Owner:    figma-builder (pdp_main) — 2026-09-15
  Updated:  figma-fixer(pdp_main) loop 1, 2026-09-15 — PM-06 (content-driven
            mobile panel/wave height, measured against the trust row) and
            PM-14 (reads the section's embedded product JSON once at init so
            customsirge:variant-change's detail.product is populated).
  Updated:  figma-fixer(pdp_main) loop 2, 2026-09-15 — PM-23 (panel-height
            measurement switched from getBoundingClientRect to offsetTop/
            offsetHeight — immune to the reveal contract's pre-settle
            translate, no more overshoot depending on WHEN it ran).
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-11 (gallery scroll-swap
            driver changed from a one-shot 40%-of-viewport threshold on the
            tiles to a continuous scroll-progress crossfade anchored on the
            whole gallery block's bottom edge; see initGallerySwap).
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-22 (decision 0055 §1):
            the CSS consuming --customsirge-pdp-gallery-swap now slides set B
            up over set A instead of crossfading opacity (assets/
            customsirge_pdp_main.css). initGallerySwap itself is UNCHANGED —
            same anchor, same progress formula, same variable name; only the
            renderer of that value moved.
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-22 rev2 (decision 0055
            §1, amended): Shahz's second review found the page itself moving
            while the images swapped, scrolling the top row out of view
            before set B arrived. initGallerySwap now reads a NEW wrapper
            (.customsirge-pdp-main__gallery-pin, CSS-sticky) instead of
            .gallery directly, and the progress formula switched from
            "gallery bottom vs viewport bottom" to "wrapper top vs the
            sticky top offset, over one fixed scroll budget" — see the
            inline comment on initGallerySwap for the full mechanism.
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-22 rev3 (decision 0055
            §1, amended a third time): Shahz's third review said pin ONLY
            the 2x2 tile grid, not the hero. initGallerySwap now reads
            .customsirge-pdp-main__tiles-pin (was .gallery-pin) and
            .customsirge-pdp-main__tiles (was .gallery) — same formula
            shape, one level deeper, hero-wrap is unpinned normal flow
            again.
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-22 FINAL (decision
            0055 §1, amended a third time, confirmed in Q&A): the freeze is
            viewport-wide. Decision 0055 literally specifies bottom-
            anchored sticky on the section; a confirmed browser rendering
            glitch on that approach (element taller than viewport, slack
            before it) meant building the same VISUAL result via .page-
            pin-frame (100vh, position:sticky;TOP:0 — rev2/rev3's proven
            anchor) with <section> absolutely bottom-aligned inside it.
            initGallerySwap's progress formula is therefore the SAME shape
            as rev2/rev3's (top-anchored), just reading .page-pin-frame /
            .page-pin instead of .gallery-pin or .tiles-pin — see the
            inline comment above initGallerySwap.
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — B-22 §4 (sticky ATC
            overlap, decision 0055 §1 follow-up, see runs/2026-09-15/v1_1/
            pdp_main/NOTES.md "Sticky-ATC trigger" finding): the sticky ATC
            bar's own show/hide trigger (assets/customsirge_sticky_atc.js,
            an IntersectionObserver on `.customsirge-pdp-main__ctas`) only
            fires when THAT element's intersection changes — during the
            freeze itself nothing about the CTAs' intersection changes (the
            whole viewport, CTAs included, holds still), so a bar already
            shown before the freeze started stayed shown, overlapping the
            frozen section's bottom ~83px for the whole pin duration.
            initGallerySwap now also tracks whether the pin is CURRENTLY
            engaged (0 < raw progress < 1, before it's clamped for the CSS
            var) and dispatches `customsirge:pdp-pin-change` (bubbles, same
            shape as customsirge:variant-change) on `root` whenever that
            flips — a signal the CTAs' own observer has no way to produce.
            sticky_atc.js listens for it and ANDs it into its own visibility
            decision (see that file's own header for the consuming half).
  Updated:  figma-fixer(pdp_main) v1.1, 2026-09-15 — F-01 BLOCKER (v1.1
            verify, VERDICT_FINAL.md): `.customsirge-pdp-main__page-pin--
            active` was rendered at page-load time in the Liquid whenever
            `has_swap_set`, so the whole-viewport freeze (B-22 FINAL above)
            was engaged from scrollY 0 on every load — this section sits at
            the top of the page, so the sticky frame's engage point (its
            own natural, un-stuck position) is reached almost immediately,
            bottom-anchoring the 1692px section inside a 100vh frame and
            hiding the title/price/buy box off-screen permanently, on every
            width >= 750. Fix: sections/customsirge_pdp_main.liquid no
            longer emits `--active` at all; initGallerySwap's `update()`
            now measures the section's own un-pinned rect each scroll frame
            and adds `--active` (via `pinWrap.classList`) only once its
            bottom edge would otherwise cross the viewport's bottom edge —
            and removes it again if the user scrolls back up above that
            point — so a no-JS visitor, and every JS visitor before that
            scroll depth, sees the section in plain normal flow. The
            existing top-anchored pin geometry (frame/spacer/absolute
            section) and progress formula are unchanged; only WHEN the
            modifier class is present changed. See the inline comments on
            `engaged`/`release()` in initGallerySwap for the mechanism.
  Updated:  2026-09-28 — pin-freeze: F-01's scroll-time `--active` toggle
            re-laid the page out as a 100vh frame and jumped by (section −
            1.7vh) px on any viewport shorter than ~1000px (client 1470×745:
            skipped straight to the next section). `--active` is now set once
            at init; the frame is a plain top-sticky box whose `top` is
            computed so the hold starts when the tile grid's bottom reaches
            the viewport bottom. No layout change at engage/release.
  Updated:  figma-fixer(pdp_main) mobile pass, 2026-09-28 — item 2:
            initMobileThumbs wires the new thumbnail row to the mobile
            carousel's existing Swiper instance (kit.js carousel component:
            el.__customsirgeSwiper / customsirge:carousel:ready); scroll-
            snap fallback when Swiper never attaches. item 4: mobile wave
            offset height-70 -> height-35 so the curve actually clears the
            panel's flat bottom edge (mobile-only branch of initPanelHeight).
  Updated:  2026-09-29 — click-to-hero (client request): initHeroSwap makes each
            desktop 2x2 tile a keyboard-operable button (role/tabindex/Enter/
            Space, "Show image N as main image"); activating it TRUE-SWAPS the
            tile's visible image with the hero (src/srcset/width/height/alt
            exchanged, each <img> keeps its own sizes, so the hero's 1:1 box
            never shifts and no image is ever lost), scrolls back to the hero
            under the sticky header (instant with reduced motion) and moves
            focus to it. Never touches the pin/freeze (initGallerySwap) —
            it only reads the swap progress to know which layer is showing.

  customsirge: WHY THIS FILE DOES ITS OWN VARIANT FETCH INSTEAD OF DAWN'S
  <product-info>/product-info.js — traced product-info.js's
  `toggleSubmitButton(disable, text)`: on every successful (non-disabled)
  variant fetch it unconditionally runs
  `this.submitButtonText.textContent = window.variantStrings.addToCart`,
  where `submitButtonText` is `submitButton.querySelector('span')` — the
  FIRST <span> in our ATC button. This section's ATC button holds TWO
  price-bearing spans (one-time / subscribe label, toggled by the plan
  cards below) so that price text never needs client-side money formatting;
  product-info.js would silently overwrite the first span's "– $14.99"
  suffix with plain "Add to cart" on every variant change. Rather than fork
  the kit's own JS (hot file) or drop the two-span design, this file
  reuses Dawn's REAL <variant-selects> element (so the pubsub/keyboard/
  a11y contract is exactly Dawn's) and does its own equivalent, narrower
  swap: price, plans and CTAs blocks by id, taken verbatim from the same
  `?section_id=` fetch Dawn uses. No media gallery / quantity-rules / pickup
  sync — this design has none of those (no thumbnails/zoom, no buy-box qty
  stepper per decision 0014), so Dawn's fuller mechanism buys nothing here.
*/
(function () {
  'use strict';

  function initGallerySwap(root) {
    var hasSwapSet = root.querySelector('.customsirge-pdp-main__tile-img--b');
    if (!hasSwapSet) return undefined;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    // customsirge: B-11 (v1.1) -- Shahz: "the second 4 images scroll too
    // early; the first 4 should stay until the user scrolls to the end of
    // that section, the next 4 show as they scroll further." Original
    // anchor was the WHOLE gallery block's BOTTOM edge vs the viewport
    // bottom -- superseded below by B-22 rev2/rev3/FINAL.
    //
    // customsirge: B-22 (v1.1, decision 0055 §1) -- the 0..1 value drives a
    // per-tile SLIDE-UP in CSS (set B transforms from translateY(100%) to
    // translateY(0)) instead of an opacity crossfade. Unaffected by any
    // revision below -- only what gets pinned while this value ramps
    // changed.
    //
    // customsirge: B-22 rev2/rev3 (2026-09-15) -- pinned the gallery, then
    // just the tile grid, via a TOP-anchored sticky wrapper. Both rejected
    // by Shahz (rev2: hero frozen too; rev3: only the grid froze, the rest
    // of the page kept scrolling) -- superseded by FINAL below. History
    // only; none of that markup/CSS exists any more.
    //
    // customsirge: B-22 FINAL (v1.1, decision 0055 §1, amended a third
    // time, confirmed in Q&A) -- "the whole viewport freezes, not just the
    // gallery." Decision 0055 literally describes `.customsirge-pdp-main`
    // (the <section>) going position:sticky; BOTTOM:0 inside a wrapper
    // with a scroll-budget spacer AFTER it. Building exactly that and
    // testing it (scratch sticky_test*.html during this fix, not shipped)
    // showed a confirmed browser rendering glitch: an element TALLER than
    // the viewport, with slack (the budget) BEFORE it in its containing
    // block, renders in a visibly broken intermediate state for the whole
    // early/not-yet-pinned scroll range -- bottom-anchored sticky's clamp
    // behaves very differently from top-anchored sticky's once the element
    // exceeds the viewport height, and rev2/rev3 already proved the TOP-
    // anchored version is reliable. So: same VISUAL result, proven
    // mechanism. assets/customsirge_pdp_main.css wraps <section> in TWO
    // boxes -- `.page-pin` (outer, gets the scroll-budget spacer) and
    // `.page-pin-frame` (inner, a fixed 100vh box that goes position:
    // sticky; TOP:0 -- rev2/rev3's exact anchor). Inside the frame,
    // <section> itself goes position:absolute; bottom:0, always bottom-
    // aligned within it. Freezing a 100vh frame at the viewport's top for
    // one budget's worth of scroll is geometrically identical to freezing
    // the (bottom-anchored) section at the viewport's bottom -- content
    // pinned to the bottom of a viewport-tall, top-pinned box cannot move
    // relative to the viewport either way -- without inheriting bottom-
    // sticky's pre-engagement glitch.
    //
    // The progress math is therefore IDENTICAL in shape to rev2/rev3's:
    // watch the frame's own top falling toward a fixed clearance from the
    // viewport's top, over one fixed budget. `.page-pin-frame` is
    // <section>'s OWN PARENT now (it wraps the section from outside), not
    // a descendant -- and `.page-pin` (the spacer's parent) is the frame's
    // OWN parent, one level further out. Neither is reachable via
    // root.querySelector (descendants only), so both are looked up via
    // root.parentElement / .parentElement.parentElement instead.
    var section = root; // root IS [data-customsirge-pdp-main], the element that visually appears frozen
    var frame = root.parentElement;
    if (!frame || !frame.classList.contains('customsirge-pdp-main__page-pin-frame')) return undefined;
    var pinWrap = frame.parentElement;
    if (!pinWrap || !pinWrap.classList.contains('customsirge-pdp-main__page-pin')) return undefined;
    var spacer = pinWrap.querySelector(':scope > .customsirge-pdp-main__page-pin-spacer');
    if (!spacer) return undefined;

    var ticking = false;
    var listening = false;
    var pinned = false;
    // customsirge: pin-freeze (2026-09-28) -- supersedes F-01's scroll-time
    // class toggle. F-01 added `--active` only once the section's bottom
    // reached the viewport bottom, then re-laid it out as a 100vh sticky
    // frame with the section absolutely bottom-aligned. On any viewport
    // shorter than section + budget (1692px section vs a 745px laptop:
    // 1.7 x 745 = 1266px of wrapper) that toggle shrank the document and
    // snapped the section up by (section - 1.7vh) px, and the progress
    // formula started past 1 -- the page skipped straight past the freeze.
    // Now: `--active` is applied ONCE here (JS-only, so no-JS stays plain
    // flow) and never toggled on scroll; the CSS makes `.page-pin-frame`
    // a plain top-sticky box of the section's own height inside a wrapper
    // of section + budget (the standard sticky pattern). The sticky `top`
    // is computed below so it engages exactly when the 2x2 tile grid's
    // bottom reaches the viewport bottom -- a negative top when the section
    // is taller than the viewport. Layout never changes at the engage /
    // release points, so scrollY and content are continuous.
    var ACTIVE_CLASS = 'customsirge-pdp-main__page-pin--active';
    var tiles = root.querySelector('.customsirge-pdp-main__tiles');
    var pinTop = null;
    pinWrap.classList.add(ACTIVE_CLASS);

    // Sticky top = viewport height minus the grid bottom's offset inside the
    // section (grid bottom lands on the viewport bottom), capped at the
    // published sticky-header height so a grid that fits early still pins
    // below the header instead of pushing the section down on load.
    function measurePinTop() {
      if (!tiles) return;
      var secTop = section.getBoundingClientRect().top;
      var gridBottom = tiles.getBoundingClientRect().bottom - secTop;
      if (gridBottom <= 0) return;
      var headerH =
        parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--customsirge-header-h')) || 0;
      var next = Math.round(Math.min(window.innerHeight - gridBottom, headerH));
      if (next === pinTop) return;
      pinTop = next;
      pinWrap.style.setProperty('--customsirge-pdp-page-pin-top', pinTop + 'px');
    }

    // customsirge: B-22 §4 -- fires customsirge:pdp-pin-change whenever the
    // freeze engages/releases, so sticky_atc.js (a different section, no
    // other way to observe this) can stop showing itself over the frozen
    // section. See this file's own header for why the bar's existing
    // IntersectionObserver can't catch this on its own.
    function setPinned(next) {
      if (next === pinned) return;
      pinned = next;
      root.dispatchEvent(
        new CustomEvent('customsirge:pdp-pin-change', { bubbles: true, detail: { pinned: pinned } })
      );
    }

    function reset() {
      root.style.setProperty('--customsirge-pdp-gallery-swap', '0');
      setPinned(false);
    }

    function update() {
      ticking = false;
      measurePinTop();
      var budget = spacer.getBoundingClientRect().height;
      if (budget <= 0) {
        // <6 media never reaches here (early-return above); reduced-motion
        // is also an early-return; this guards the one remaining case --
        // the CSS custom property collapsed the budget to 0 some other way
        // (e.g. a merchant override, or < 750px where the pin CSS is inert)
        // -- fail open to "no swap".
        reset();
        return;
      }
      // customsirge: read pinWrap's rect, NOT frame's -- frame is the
      // STICKY element itself, so its own rect.top is clamped constant
      // (== clearance) for the entire pin duration (that's the whole
      // point -- it's what freezes the section). pinWrap (.page-pin) is a
      // plain, non-sticky block that keeps scrolling normally underneath
      // the pinned frame the whole time, so ITS top edge is the live,
      // monotonically-decreasing scroll signal this needs -- exactly the
      // relationship rev2/rev3 used between their wrapper and the sticky
      // child inside it.
      var clearance = parseFloat(window.getComputedStyle(frame).top) || 0;
      var wrapTop = pinWrap.getBoundingClientRect().top;
      var rawProgress = (clearance - wrapTop) / budget;
      if (rawProgress <= 0) {
        reset();
        return;
      }
      // customsirge: B-22 §4 -- "pinned" is the OPEN interval (0, 1): at and
      // after 1 it has already released (natural sticky release; `--active`
      // is left on -- verified in NOTES.md's B-22 FINAL table that this is
      // harmless because everything genuinely resumes normal scroll once
      // `pinWrap` runs out of budget, `--active` or not). Using the
      // unclamped rawProgress here (not the clamped `progress` below,
      // which is permanently pinned to 0 or 1 outside the budget and can't
      // tell "before" from "after") is what makes this distinction
      // possible.
      setPinned(rawProgress > 0 && rawProgress < 1);
      var progress = rawProgress;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;
      root.style.setProperty('--customsirge-pdp-gallery-swap', String(progress));
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !listening) {
          listening = true;
          window.addEventListener('scroll', onScroll, { passive: true });
          update();
        } else if (!entry.isIntersecting && listening) {
          listening = false;
          window.removeEventListener('scroll', onScroll);
          setPinned(false);
        }
      });
    });
    io.observe(pinWrap);
    window.addEventListener('resize', onScroll, { passive: true });
    measurePinTop();

    return function cleanup() {
      io.disconnect();
      if (listening) window.removeEventListener('scroll', onScroll);
      // customsirge: drop the modifier class + computed top on teardown
      // (theme editor re-render/section removal).
      pinWrap.classList.remove(ACTIVE_CLASS);
      pinWrap.style.removeProperty('--customsirge-pdp-page-pin-top');
      window.removeEventListener('resize', onScroll);
      setPinned(false);
    };
  }

  /* customsirge: click-to-hero (client request 2026-09-29). Desktop only in
     practice -- .tiles is display:none below 750px, so the mobile carousel +
     thumbs are untouched. True swap rather than "show in hero": the hero's
     image moves into the clicked tile, so every media stays reachable. The
     attributes are applied in JS (not Liquid) so a no-JS visitor never sees
     a role=button that does nothing. */
  function initHeroSwap(root) {
    var hero = root.querySelector('.customsirge-pdp-main__hero-wrap .customsirge-pdp-main__hero');
    var heroImg = hero && hero.querySelector('img');
    var tiles = Array.prototype.slice.call(root.querySelectorAll('.customsirge-pdp-main__tile'));
    if (!heroImg || !tiles.length) return undefined;

    var reduceQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    var SWAP_ATTRS = ['src', 'srcset', 'width', 'height', 'alt'];
    var ACTIVE = 'customsirge-pdp-main__tile--swappable';

    // 1-based product-media position travels WITH the image through swaps,
    // so the label always names what the tile currently shows.
    heroImg.setAttribute('data-customsirge-media-pos', '1');
    tiles.forEach(function (tile, i) {
      var a = tile.querySelector('.customsirge-pdp-main__tile-img--a');
      var b = tile.querySelector('.customsirge-pdp-main__tile-img--b');
      if (a) a.setAttribute('data-customsirge-media-pos', String(i + 2));
      if (b) b.setAttribute('data-customsirge-media-pos', String(i + 6));
    });

    // Which layer the tile shows: set B once the scroll-swap (initGallerySwap,
    // --customsirge-pdp-gallery-swap) is past halfway; always A with reduced
    // motion (CSS holds B off-frame there).
    function visibleImg(tile) {
      var b = tile.querySelector('.customsirge-pdp-main__tile-img--b');
      var swap = parseFloat(root.style.getPropertyValue('--customsirge-pdp-gallery-swap')) || 0;
      if (b && swap >= 0.5 && !(reduceQuery && reduceQuery.matches)) return b;
      return tile.querySelector('.customsirge-pdp-main__tile-img--a');
    }

    function label(tile) {
      var img = visibleImg(tile);
      var pos = img && img.getAttribute('data-customsirge-media-pos');
      tile.setAttribute('aria-label', 'Show image ' + (pos || '') + ' as main image');
    }
    function labelAll() {
      tiles.forEach(label);
    }

    function swapInto(img) {
      SWAP_ATTRS.concat('data-customsirge-media-pos').forEach(function (name) {
        var h = heroImg.getAttribute(name);
        var t = img.getAttribute(name);
        if (t === null) heroImg.removeAttribute(name);
        else heroImg.setAttribute(name, t);
        if (h === null) img.removeAttribute(name);
        else img.setAttribute(name, h);
      });
    }

    // Document y that puts the hero's top just under the sticky header. The
    // hero may be inside the (possibly stuck/released) sticky frame, so its
    // un-stuck position is read off the non-sticky .page-pin wrapper plus the
    // hero's offset inside the frame; capped at the pin's engage point so
    // the scroll never lands inside the freeze.
    function heroTargetY() {
      var headerH =
        parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('--customsirge-header-h')) || 0;
      var frame = root.parentElement;
      var pinWrap = frame && frame.parentElement;
      var heroTop = hero.getBoundingClientRect().top;
      var y;
      if (pinWrap && pinWrap.classList.contains('customsirge-pdp-main__page-pin')) {
        var wrapTop = pinWrap.getBoundingClientRect().top;
        y = window.scrollY + wrapTop + (heroTop - frame.getBoundingClientRect().top) - headerH;
        var pinTop = parseFloat(pinWrap.style.getPropertyValue('--customsirge-pdp-page-pin-top'));
        if (!isNaN(pinTop)) y = Math.min(y, window.scrollY + wrapTop - pinTop);
      } else {
        y = window.scrollY + heroTop - headerH;
      }
      return Math.max(0, Math.round(y));
    }

    var pendingFocus = null;
    function focusHero() {
      pendingFocus = null;
      window.removeEventListener('scrollend', focusHero);
      hero.focus({ preventScroll: true });
    }

    function activate(tile) {
      var img = visibleImg(tile);
      if (!img) return;
      swapInto(img);
      labelAll();
      var y = heroTargetY();
      var instant = reduceQuery && reduceQuery.matches;
      if (pendingFocus) window.clearTimeout(pendingFocus);
      window.removeEventListener('scrollend', focusHero);
      if (instant || Math.abs(window.scrollY - y) < 2) {
        window.scrollTo({ top: y, behavior: 'instant' });
        focusHero();
        return;
      }
      window.addEventListener('scrollend', focusHero);
      // fallback for engines without scrollend
      pendingFocus = window.setTimeout(focusHero, 1200);
      window.scrollTo({ top: y, behavior: 'smooth' });
    }

    function onClick(event) {
      activate(event.currentTarget);
    }
    function onKey(event) {
      if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      activate(event.currentTarget);
    }
    function onFocus(event) {
      label(event.currentTarget);
    }

    hero.setAttribute('tabindex', '-1');
    tiles.forEach(function (tile) {
      tile.setAttribute('role', 'button');
      tile.setAttribute('tabindex', '0');
      tile.classList.add(ACTIVE);
      tile.addEventListener('click', onClick);
      tile.addEventListener('keydown', onKey);
      tile.addEventListener('focus', onFocus);
    });
    labelAll();

    // keep labels truthful as the scroll-swap flips a tile from set A to B
    var lastSide = null;
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      // two frames: initGallerySwap writes the progress in ITS rAF, which may
      // be queued after this one (its scroll listener attaches later)
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(relabel);
      });
    }
    function relabel() {
      ticking = false;
      var side = (parseFloat(root.style.getPropertyValue('--customsirge-pdp-gallery-swap')) || 0) >= 0.5;
      if (side === lastSide) return;
      lastSide = side;
      labelAll();
    }
    window.addEventListener('scroll', onScroll, { passive: true });

    return function cleanup() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('scrollend', focusHero);
      if (pendingFocus) window.clearTimeout(pendingFocus);
      hero.removeAttribute('tabindex');
      tiles.forEach(function (tile) {
        tile.removeAttribute('role');
        tile.removeAttribute('tabindex');
        tile.removeAttribute('aria-label');
        tile.classList.remove(ACTIVE);
        tile.removeEventListener('click', onClick);
        tile.removeEventListener('keydown', onKey);
        tile.removeEventListener('focus', onFocus);
      });
    };
  }

  function initPlans(root) {
    var inputs = Array.prototype.slice.call(root.querySelectorAll('[data-customsirge-plan-input]'));
    if (!inputs.length) return undefined;
    var select = root.querySelector('[data-customsirge-plan-select]');

    function sync() {
      var checked = null;
      inputs.forEach(function (input) {
        var label = root.querySelector('label[for="' + input.id + '"]');
        if (label) label.classList.toggle('customsirge-pdp-main__plan--selected', input.checked);
        if (input.checked) checked = input;
      });
      // Default to 'onetime' whenever no radio is checked yet AND when a
      // checked radio has no plan attribute for some reason -- an ATC label
      // toggle that can't determine the plan must fail open to the always-
      // present one-time span, never hide every span (a real bug found here:
      // the attribute briefly lived on the <label> only, not this <input>).
      var plan = (checked && checked.getAttribute('data-customsirge-plan')) || 'onetime';
      root.querySelectorAll('[data-customsirge-atc-label]').forEach(function (el) {
        el.hidden = el.getAttribute('data-customsirge-atc-label') !== plan;
      });
      // Keep displayed subscription prices aligned with the selected Shopify allocation.
      if (plan === 'subscribe' && checked) {
        var chosen = root.querySelector('[data-customsirge-freq-opt="' + checked.value + '"]');
        if (chosen) {
          var price = chosen.getAttribute('data-gigli-plan-price');
          var saving = Number(chosen.getAttribute('data-gigli-plan-saving')) || 0;
          var priceEl = root.querySelector('[data-gigli-plan-price]:not(button)');
          var compareEl = root.querySelector('[data-gigli-plan-compare]');
          var titleEl = root.querySelector('label[data-customsirge-plan="subscribe"] .customsirge-pdp-main__plan-title');
          var atcEl = root.querySelector('[data-customsirge-atc-label="subscribe"]');
          if (priceEl && price) priceEl.textContent = price;
          if (compareEl) compareEl.hidden = saving <= 0;
          if (titleEl) titleEl.textContent = saving > 0 ? 'Subscribe & save ' + saving + '%' : 'Subscribe';
          if (atcEl && chosen.getAttribute('data-gigli-plan-atc')) atcEl.textContent = chosen.getAttribute('data-gigli-plan-atc');
        }
      }
    }

    function onChange() {
      sync();
      syncFreq();
    }

    inputs.forEach(function (input) {
      input.addEventListener('change', onChange);
    });

    /* 2026-10-09: frequency tiles. A tile sets the subscribe radio's plan and
       selects it; the active tile follows the radio's value. */
    var subscribeInput = inputs.filter(function (i) {
      return i.getAttribute('data-customsirge-plan') === 'subscribe';
    })[0];
    var freqOpts = Array.prototype.slice.call(root.querySelectorAll('[data-customsirge-freq-opt]'));
    function syncFreq() {
      if (!subscribeInput) return;
      freqOpts.forEach(function (opt) {
        var on = opt.getAttribute('data-customsirge-freq-opt') === subscribeInput.value;
        opt.classList.toggle('customsirge-pdp-main__freq-opt--active', on);
        opt.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }
    function onFreqClick(e) {
      if (!subscribeInput) return;
      subscribeInput.value = e.currentTarget.getAttribute('data-customsirge-freq-opt');
      subscribeInput.checked = true;
      subscribeInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    freqOpts.forEach(function (opt) { opt.addEventListener('click', onFreqClick); });
    syncFreq();

    var onSelectChange;
    if (select) {
      onSelectChange = function () {
        var subscribeInput = inputs.filter(function (i) {
          return i.getAttribute('data-customsirge-plan') === 'subscribe';
        })[0];
        if (!subscribeInput) return;
        subscribeInput.value = select.value;
        subscribeInput.checked = true;
        subscribeInput.dispatchEvent(new Event('change', { bubbles: true }));
      };
      select.addEventListener('change', onSelectChange);
    }

    sync();

    return function cleanup() {
      inputs.forEach(function (input) { input.removeEventListener('change', onChange); });
      freqOpts.forEach(function (opt) { opt.removeEventListener('click', onFreqClick); });
      if (select && onSelectChange) select.removeEventListener('change', onSelectChange);
    };
  }

  function initVariantSync(root, rebindPlans) {
    // Bare identifiers, not window.* — constants.js/pubsub.js declare
    // PUB_SUB_EVENTS/subscribe with const/function at classic-script top
    // level; that puts them in the shared global LEXICAL scope (visible to
    // this later script by name) without adding a `window` property.
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return undefined;

    var sectionId = root.getAttribute('data-section-id');
    var productUrl = root.getAttribute('data-product-url');
    if (!sectionId || !productUrl) return undefined;

    var abortController;

    // An app block inside a swapped region (the review app's star rating in the
    // price row) is drawn by the app's own script after load; the fetched HTML
    // only carries its empty placeholder, so the live node is kept in its place.
    function swapById(html, id) {
      var next = html.getElementById(id);
      var current = document.getElementById(id);
      if (!next || !current) return;
      var kept = Array.prototype.slice.call(current.querySelectorAll('[data-customsirge-pdp-app]'));
      current.innerHTML = next.innerHTML;
      kept.forEach(function (node) {
        var fresh = current.querySelector('[data-customsirge-pdp-app="' + node.getAttribute('data-customsirge-pdp-app') + '"]');
        if (fresh) fresh.replaceWith(node);
      });
    }

    function onOptionValueChange(payload) {
      var data = payload && payload.data;
      var target = data && data.target;
      if (!target || !root.contains(target)) return;

      var selectedOptionValues = data.selectedOptionValues || [];
      var params = ['section_id=' + encodeURIComponent(sectionId)];
      if (selectedOptionValues.length) params.push('option_values=' + selectedOptionValues.join(','));
      var requestUrl = productUrl + '?' + params.join('&');

      if (abortController) abortController.abort();
      abortController = new AbortController();

      fetch(requestUrl, { signal: abortController.signal })
        .then(function (response) { return response.text(); })
        .then(function (responseText) {
          var html = new DOMParser().parseFromString(responseText, 'text/html');

          var currentVariantSelects = root.querySelector('variant-selects');
          var nextVariantSelects = html.querySelector('variant-selects');
          if (currentVariantSelects && nextVariantSelects) {
            currentVariantSelects.innerHTML = nextVariantSelects.innerHTML;
          }

          swapById(html, 'customsirge-pdp-price-' + sectionId);
          swapById(html, 'customsirge-pdp-plans-' + sectionId);
          swapById(html, 'customsirge-pdp-ctas-' + sectionId);
          // The plans/ctas swap above just replaced the DOM nodes `initPlans`
          // bound its listeners to — rebind against the fresh nodes rather
          // than relying on the outer component's one-time idempotent init.
          if (typeof rebindPlans === 'function') rebindPlans();

          var variantScript = html.querySelector('variant-selects [data-selected-variant]');
          var variant = variantScript ? JSON.parse(variantScript.textContent) : null;
          if (variant) {
            root.dispatchEvent(
              new CustomEvent('customsirge:variant-change', {
                bubbles: true,
                detail: { variant: variant, product: root.__customsirgeProduct || null },
              })
            );
          }
        })
        .catch(function (error) {
          if (error.name !== 'AbortError') console.error('[customsirge] pdp_main variant fetch failed', error);
        });
    }

    var unsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, onOptionValueChange);

    return function cleanup() {
      if (typeof unsubscribe === 'function') unsubscribe();
      if (abortController) abortController.abort();
    };
  }

  // PM-14: customsirge:variant-change's `detail.product` (client answer
  // 2026-09-15 — pdp_benefits_hero swaps its per-variant can from the whole
  // product, not just the fetched variant) was always null because nothing
  // ever set root.__customsirgeProduct. Read the JSON script the section
  // now embeds once, at init, rather than re-parsing it on every fetch.
  function readProductPayload(root) {
    var script = root.querySelector('[data-customsirge-pdp-product]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('[customsirge] pdp_main product payload failed to parse', e);
      return null;
    }
  }

  // PM-06: the white "panel" + wave hand-off is fixed-height below 750px
  // (spec's derived rule), but mobile content height varies (plan-card
  // count, rich-text accordion bodies). Measure the live gap between the
  // panel's own top and the trust row's bottom edge and write it as a CSS
  // custom property, instead of a hardcoded height that cuts across
  // whatever happens to sit at that fixed px on THIS product/viewport.
  //
  // PM-23 (loop 2): trust itself carries data-customsirge-reveal, so before
  // it settles, kit.css's reveal contract paints it `translate: 0 28px` off
  // its true layout position — getBoundingClientRect() (a PAINT-time box)
  // reflects that offset, getBoundingClientRect-based measurement did, so
  // the panel could overshoot the trust row by however much of the 28px
  // travel hadn't settled yet when a resize/load/fonts callback happened to
  // fire. offsetTop/offsetHeight are LAYOUT values `translate` never
  // touches, so walking the offsetParent chain instead is immune to reveal
  // timing entirely — no need to wait for transitionend.
  function layoutTop(el, ancestor) {
    var top = 0;
    var node = el;
    while (node && node !== ancestor && node !== document.body) {
      top += node.offsetTop || 0;
      node = node.offsetParent;
    }
    return top;
  }

  function initPanelHeight(root) {
    var panel = root.querySelector('.customsirge-pdp-main__panel');
    var trust = root.querySelector('.customsirge-pdp-main__trust');
    if (!panel || !trust) return undefined;

    var desktopQuery = window.matchMedia('(min-width: 750px)');

    function update() {
      // Desktop's panel/wave are fixed, spec-exact values set in CSS — this
      // measurement is mobile-only.
      if (desktopQuery.matches) return;
      var panelTop = layoutTop(panel, root);
      var trustBottom = layoutTop(trust, root) + trust.offsetHeight;
      var height = trustBottom - panelTop;
      if (height <= 0) return;
      root.style.setProperty('--customsirge-pdp-panel-h', height + 'px');
      // customsirge: mobile PDP item 4 (2026-09-28) -- was `height - 70`,
      // copied from desktop's wave_top = panel_bottom - 70. But `height` here
      // is the panel's HEIGHT, and the mobile panel starts at top:20px, so
      // the wave sat 90px above the panel's bottom edge and its white curve
      // (at most 88px tall once the 1440-wide path is squeezed into a phone
      // width) never cleared the flat panel edge: no wave, a flat white/pink
      // line, and the love block's 110px padding read as empty pink. Figma
      // 420 (pdp_main@420.png) draws the pink meeting the panel's flat
      // bottom at the right edge and rising 33px into the white at the left;
      // `height - 35` puts the curve's deepest point 33px below the flat
      // edge and its shallow end 2px above it. Matches the CSS fallback
      // (1247 = 1282 - 35) in assets/customsirge_pdp_main.css.
      root.style.setProperty('--customsirge-pdp-wave-top', (height - 35) + 'px');
    }

    var ticking = false;
    function scheduleUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    update();
    window.addEventListener('resize', scheduleUpdate);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', update);

    // customsirge: a single synchronous measurement at init can be stale —
    // web-font swap (title/eyebrow/plan-perk line-wrap changing) or a late
    // image reflow can move the trust row AFTER this runs, without ever
    // firing a window 'resize'. ResizeObserver on `.buy` (the flex column
    // containing everything from rating through the accordions) re-measures
    // whenever ANY of its children's height changes for ANY reason, and
    // `document.fonts`/`load` are cheap belt-and-suspenders for engines
    // where that swap doesn't trigger a layout ResizeObserver sees.
    var buy = root.querySelector('.customsirge-pdp-main__buy');
    var ro;
    if (buy && window.ResizeObserver) {
      ro = new ResizeObserver(scheduleUpdate);
      ro.observe(buy);
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
    window.addEventListener('load', update);

    return {
      update: update,
      cleanup: function () {
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('load', update);
        if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', update);
        if (ro) ro.disconnect();
      },
    };
  }

  /* "Why You'll Love It" starts level with the gallery's thumbnail row. The two
     are in different grid columns — tiles under a hero sized by aspect-ratio,
     this block under a column of text — so the offset between them changes with
     viewport width, with the product's title wrapping, with a variant swap that
     shows or hides the plan cards. CSS cannot read across columns, so measure:
     zero the padding, take both tops, and write the difference back. */
  function initLoveAlign(root) {
    var love = root.querySelector('.customsirge-pdp-main__love');
    var tiles = root.querySelector('.customsirge-pdp-main__tiles');
    if (!love || !tiles) return undefined;

    var desktopQuery = window.matchMedia('(min-width: 750px)');
    /* Mobile stacks the gallery above the buy column, so there is no second
       column to align to and the CSS fallback is the right spacing. */
    var MIN_PAD = 24;

    function update() {
      if (!desktopQuery.matches) {
        root.style.removeProperty('--customsirge-pdp-love-pad');
        return;
      }
      /* Measure with the offset neutralised, so what comes back is where the
         block would sit unaided rather than where the last measurement put it. */
      root.style.setProperty('--customsirge-pdp-love-pad', '0px');
      var delta = layoutTop(tiles, root) - layoutTop(love, root);
      /* A floor, because the alignment is a nicety and not being shoved into
         the trust row above is not: a tall buy column (long title, plan cards
         open) can put this block BELOW the tiles, and a negative padding would
         pull it up over its neighbour. */
      root.style.setProperty('--customsirge-pdp-love-pad', Math.max(MIN_PAD, delta) + 'px');
    }

    var ticking = false;
    function scheduleUpdate() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        update();
      });
    }

    update();
    window.addEventListener('resize', scheduleUpdate);
    if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', update);

    /* Same reasoning as initPanelHeight's observer: either column can change
       height after init — font swap, a late image, a variant fetch rebuilding
       the plan cards — and none of those fire a window resize. */
    var ro;
    var gallery = root.querySelector('.customsirge-pdp-main__gallery');
    var buy = root.querySelector('.customsirge-pdp-main__buy');
    if (window.ResizeObserver) {
      ro = new ResizeObserver(scheduleUpdate);
      [gallery, buy].forEach(function (n) {
        if (n) ro.observe(n);
      });
    }
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleUpdate);
    window.addEventListener('load', scheduleUpdate);

    return {
      update: update,
      cleanup: function () {
        window.removeEventListener('resize', scheduleUpdate);
        window.removeEventListener('load', scheduleUpdate);
        if (desktopQuery.removeEventListener) desktopQuery.removeEventListener('change', update);
        if (ro) ro.disconnect();
      },
    };
  }

  /* customsirge: mobile PDP item 2 (2026-09-28) -- thumbnail row under the
     mobile carousel. The carousel is the shared customsirge_carousel snippet;
     kit.js's carousel component creates the Swiper and exposes it as
     el.__customsirgeSwiper + a customsirge:carousel:ready event, so this only
     maps thumb clicks to slideTo() and mirrors slideChange back onto the
     thumbs. If Swiper never attaches (kit's scroll-snap fallback), the
     viewport is a plain horizontal scroller and the same index math works on
     its scrollLeft. Nothing here runs at >= 750px in practice: .gallery-mobile
     is display:none there, so the thumbs are never visible or clickable. */
  function initMobileThumbs(root) {
    var gallery = root.querySelector('.customsirge-pdp-main__gallery-mobile');
    if (!gallery) return undefined;
    var thumbs = Array.prototype.slice.call(gallery.querySelectorAll('[data-customsirge-pdp-thumb]'));
    var carousel = gallery.querySelector('[data-customsirge-carousel]');
    if (!thumbs.length || !carousel) return undefined;
    var viewport = carousel.querySelector('.customsirge-carousel__viewport');
    var slides = carousel.querySelectorAll('.customsirge-carousel__slide');
    var row = thumbs[0].parentNode;
    var swiper = null;

    function setCurrent(index) {
      thumbs.forEach(function (thumb, k) {
        var on = k === index;
        thumb.classList.toggle('customsirge-pdp-main__thumb--current', on);
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      // keep the current thumb inside the (scrollable) row
      var cur = thumbs[index];
      if (!cur) return;
      var left = cur.offsetLeft - row.offsetLeft;
      if (left < row.scrollLeft) row.scrollLeft = left;
      else if (left + cur.offsetWidth > row.scrollLeft + row.clientWidth) row.scrollLeft = left + cur.offsetWidth - row.clientWidth;
    }

    function goTo(index) {
      if (swiper) {
        swiper.slideTo(index);
        return;
      }
      if (viewport && slides[index]) {
        viewport.scrollTo({ left: slides[index].offsetLeft, behavior: 'smooth' });
        setCurrent(index);
      }
    }

    function onThumbClick(event) {
      goTo(parseInt(event.currentTarget.getAttribute('data-customsirge-pdp-thumb'), 10) || 0);
    }
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', onThumbClick);
    });

    function onSlideChange() {
      setCurrent(swiper.realIndex);
    }
    function bind(instance) {
      if (swiper || !instance) return;
      swiper = instance;
      swiper.on('slideChange', onSlideChange);
      setCurrent(swiper.realIndex);
    }
    function onReady(event) {
      bind(event.detail && event.detail.swiper);
    }
    bind(carousel.__customsirgeSwiper);
    carousel.addEventListener('customsirge:carousel:ready', onReady);

    // scroll-snap fallback (no Swiper): track the slide under the viewport
    var ticking = false;
    function onViewportScroll() {
      if (swiper || ticking || !slides.length) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        var w = slides[0].offsetWidth || 1;
        setCurrent(Math.max(0, Math.min(slides.length - 1, Math.round(viewport.scrollLeft / w))));
      });
    }
    if (viewport) viewport.addEventListener('scroll', onViewportScroll, { passive: true });

    return function cleanup() {
      thumbs.forEach(function (thumb) {
        thumb.removeEventListener('click', onThumbClick);
      });
      carousel.removeEventListener('customsirge:carousel:ready', onReady);
      if (viewport) viewport.removeEventListener('scroll', onViewportScroll);
      if (swiper && typeof swiper.off === 'function') swiper.off('slideChange', onSlideChange);
    };
  }

  Customsirge.register('pdp_main', {
    selector: '[data-customsirge-pdp-main]',
    init: function (root) {
      root.__customsirgeProduct = readProductPayload(root);

      var gallerySwapTeardown = initGallerySwap(root);
      var plansTeardown = initPlans(root);
      var panelHeight = initPanelHeight(root);
      var loveAlign = initLoveAlign(root);
      var thumbsTeardown = initMobileThumbs(root);
      var heroSwapTeardown = initHeroSwap(root);

      function rebindPlans() {
        if (typeof plansTeardown === 'function') plansTeardown();
        plansTeardown = initPlans(root);
        // A variant fetch can show/hide the plan cards (or the frequency
        // <select>, PM-16), changing how tall the white panel needs to be.
        if (panelHeight) panelHeight.update();
        if (loveAlign) loveAlign.update();
      }

      var variantSyncTeardown = initVariantSync(root, rebindPlans);

      return function cleanup() {
        [gallerySwapTeardown, plansTeardown, variantSyncTeardown, thumbsTeardown, heroSwapTeardown].forEach(function (fn) {
          if (typeof fn === 'function') fn();
        });
        if (panelHeight) panelHeight.cleanup();
        if (loveAlign) loveAlign.cleanup();
      };
    },
  });
})();
